<template>
  <div
    class="board_view"
    :class="[cd.name, isDisabled ? 'board_view_disabled' : '', cd.deviceType === DeviceType.keyboard ? 'keyboard_view' : 'mouse_view']"
    id="virtual_device"
    :style="cd.deviceType === DeviceType.mouse ? boardStyle : {}"
    @mouseup="handleMouseUp"
    @mousedown="handleMouseDown"
  >
    <div v-for="(row, index) in keyDataAndLayout" :key="index" class="device_row">
      <div
        v-for="[kk, vk] in row"
        :key="vk"
        class="device_key"
        :class="[cd.getKeyClassName(kk), cd.deviceType === DeviceType.keyboard ? 'keyboard_view_key' : '', resolveKeyClass(kk, vk)]"
        :style="keyStyles[kk]"
        :id="vk.toString()"
        @click="handleKeyClick(kk, vk)"
        @mouseenter="handleKeyMouseEnter($event, kk, vk)"
        @mouseleave="handleKeyMouseLeave($event, kk, vk)"
        @mousedown="handleKeyMouseDown($event, kk, vk)"
      >
        <!-- key 确保每个插槽内容的唯一性 -->
        <slot :kk="kk" :vk="vk" :key="vk"></slot>
        <div class="texture" v-if="isShowTexture && cd.deviceType === DeviceType.keyboard && getTexture(vk).length > 0">{{ getTexture(vk) }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentDevice } from '@/profiles/selector'
import { computed, onBeforeUnmount, onMounted, PropType, StyleValue, watch } from 'vue'
import { KK, VK } from '@/define/define'
import { DeviceType } from '@libs/types/enum'
import { operate, OperateType } from '@/service/driver/operate'

type DeviceKeyClass = string | string[] | Record<string, boolean>

const props = defineProps({
  isShowTexture: {
    type: Boolean as PropType<boolean>,
    default: true,
    required: false,
  },
  // 是否禁用按键
  isDisabled: {
    type: Boolean as PropType<boolean>,
    default: false,
    required: false,
  },
  //是否支持连续选择
  isContinuousSelect: {
    type: Boolean as PropType<boolean>,
    default: false,
    required: false,
  },
  keyClassResolver: {
    type: Function as PropType<(kk: KK, vk: VK) => DeviceKeyClass>,
    required: false,
    default: undefined,
  },
})
const keyDataAndLayout = computed(() => cd.value.getKeyDataAndLayout())

const cd = computed(() => getCurrentDevice.value)

const getTexture = (vk: VK): string => {
  if (cd.value.texture[vk]) {
    return cd.value.texture[vk].text || ''
  }
  return ''
}

const resolveKeyClass = (kk: KK, vk: VK): DeviceKeyClass => {
  if (!props.keyClassResolver) {
    return ''
  }

  return props.keyClassResolver(kk, vk)
}

const boardStyle = computed((): StyleValue => {
  return {
    width: `${cd.value.width}px`,
    backgroundImage: cd.value.boardImage ? `url(${cd.value.boardImage})` : 'none',
    paddingLeft: `${cd.value.paddingX}px`,
    paddingTop: `${cd.value.paddingY}px`,
    paddingBottom: `${cd.value.paddingBottom}px`,
  }
})

const keyStyles = computed(() => {
  const styles: Record<number, StyleValue> = {}
  keyDataAndLayout.value.forEach((row) => {
    row.forEach((_vk, kk) => {
      const isAbsolute = cd.value.isAbsolute(kk)
      const square = cd.value.getKeySquare(kk)
      if (isAbsolute) {
        styles[kk] = {
          position: 'absolute',
          left: `${square.x}px`,
          top: `${square.y}px`,
          width: `${square.w}px`,
          height: `${square.h}px`,
        }
      } else {
        styles[kk] = {
          width: `${square.w}px`,
          height: `${square.h}px`,
          marginLeft: `${square.x}px`,
          marginTop: `${square.y}px`,
        }
      }
    })
  })
  return styles
})

const emits = defineEmits<{
  (e: 'keyClick', kk: KK, vk: VK): void
  (e: 'continuousSelectKey', kks: KK[]): void
  (e: 'keyMouseEnter', event: MouseEvent, kk: KK, vk: VK): void
  (e: 'keyMouseLeave', event: MouseEvent, kk: KK, vk: VK): void
}>()

// 移入
const handleKeyMouseEnter = (event: MouseEvent, kk: KK, vk: VK) => {
  continuousSelectKey(kk, vk)
  emits('keyMouseEnter', event, kk, vk)
}
// 移出
const handleKeyMouseLeave = (event: MouseEvent, kk: KK, vk: VK) => {
  emits('keyMouseLeave', event, kk, vk)
}
// 按下
const handleKeyMouseDown = (_event: MouseEvent, kk: KK, _vk: VK) => {
  handleContinuousSelectData(kk)
}
// 按键的点击事件
const handleKeyClick = (kk: KK, vk: VK) => {
  console.log(`kk:0x${kk.toString(16)}\nvk:0x${vk.toString(16)}`)
  emits('keyClick', kk, vk)
}
let selectedKeys: KK[] = []
let selectedAdd: boolean | null = null
let isMouseDown: boolean | null = null

watch(
  () => props.isContinuousSelect,
  (newVal: boolean, oldVal: boolean) => {
    if (newVal != oldVal) {
      clearContiguousSelection()
    }
  },
)
const clearContiguousSelection = () => {
  selectedKeys = []
}
// 将方法或者属性暴露给使用的组件
defineExpose({
  clearContiguousSelection,
})

const continuousSelectKey = (kk: KK, _vk: VK) => {
  if (!isMouseDown) return
  if (!props.isContinuousSelect) return

  handleContinuousSelectData(kk)
  emits('continuousSelectKey', selectedKeys)
}

const handleContinuousSelectData = (kk: KK) => {
  if (selectedAdd === null) {
    selectedAdd = !selectedKeys.includes(kk)
  }
  if (selectedAdd) {
    if (selectedKeys.includes(kk)) return
    selectedKeys.push(kk)
  } else {
    selectedKeys = selectedKeys.filter((key) => key !== kk) // 取消选中
  }
}
onMounted(() => {
  document.addEventListener('mousedown', handleMouseDown)
  document.addEventListener('mouseup', handleMouseUp)
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleMouseDown)
  document.removeEventListener('mouseup', handleMouseUp)
})
const handleMouseUp = () => {
  isMouseDown = false
  selectedAdd = null
}
const handleMouseDown = () => {
  isMouseDown = true
}
</script>

<style lang="less" scoped>
@hover_color: var(--color-brand-1);
@white_color: var(--color-white);

.board_view_disabled {
  pointer-events: none;
  cursor: not-allowed !important;
}

.board_view {
  display: flex;
  flex-direction: column;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: auto;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  background-color: transparent;
  border-radius: var(--radius-l);
  width: fit-content;

  &.keyboard_view {
    padding: 24px;
    background-color: rgba(255, 255, 255, 0.82);
    border-radius: var(--radius-l);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
    border: none;
  }

  // &.HE2,
  // &.HE3,
  // &.HE3_SE,
  // &.HE6_Ultra,
  // &.Amber_35_8,
  // &.Amber_35_8_Wired {
  //   height: 400px;
  // }

  .device_row {
    display: flex;
    flex-direction: row;
    width: 100%;
    position: relative; /* 为 z-index 提供上下文 */

    &:hover {
      z-index: 999;
    }
  }

  .device_key:has(.disabled) {
    pointer-events: none;
    cursor: not-allowed;
    box-shadow: none;

    &.keyboard_view_key {
      background-color: var(--color-gray-1);
      border-color: transparent;
    }

    .texture {
      color: var(--color-text-hint);
    }

    &.white {
      background-color: var(--bg-subtle);
    }
  }

  .device_key:has(.mgx_key_type.disabled) {
    opacity: 0.5;

    &.keyboard_view_key {
      background-color: #fff;
      border-color: rgba(0, 0, 0, 0.02);
      box-shadow:
        0 2px 6px rgba(0, 0, 0, 0.04),
        0 1px 2px rgba(0, 0, 0, 0.06);
    }

    .texture {
      color: var(--color-dark);
      opacity: 0.5;
    }

    &.white {
      opacity: 0.8;
      background-color: #fff;
      border-color: rgba(0, 0, 0, 0.02);
    }
  }

  .device_key.device_key_clear_bg,
  .device_key.device_key_mgx_calibration {
    background: transparent !important;
    box-shadow: none !important;
  }

  .device_key {
    cursor: pointer;
    position: relative;
    border-radius: var(--radius-s);
    text-align: center;
    background-color: transparent;
    font-weight: bold;
    font-size: 8px;
    transition:
      transform 0.16s ease,
      box-shadow 0.16s ease,
      background-color 0.16s ease;

    &.keyboard_view_key {
      background-color: #fff;
      box-shadow:
        0 2px 6px rgba(0, 0, 0, 0.04),
        0 1px 2px rgba(0, 0, 0, 0.06);
      border: 1px solid rgba(0, 0, 0, 0.02);

      &:hover {
        transform: translateY(-1px);
        z-index: 2;
        background-color: #fff;
        box-shadow:
          0 6px 12px rgba(0, 0, 0, 0.07),
          0 2px 4px rgba(0, 0, 0, 0.04);
      }

      &.mgx_key_selected:hover {
        background: var(--brand-color);
        box-shadow: var(--action-btn-primary-shadow);
      }
    }

    .texture {
      position: absolute;
      top: 0px;
      padding-top: 3px;
      left: 0px;
      bottom: 0px;
      right: 0px;
      text-align: center;
      color: var(--color-dark);
      transition: color 0.16s ease;
    }

    &.mgx_key_selected {
      background: var(--brand-color);
      box-shadow: var(--action-btn-primary-shadow);

      .texture {
        color: var(--color-white) !important;
        opacity: 1;
      }

      .mgx_key_type {
        color: var(--color-white);
        -webkit-text-fill-color: var(--color-white);
        opacity: 1;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
      }
    }

    &.action_key_remapped,
    &.action_key_fn_remapped {
      color: var(--color-white) !important;
      font-weight: bold;

      .texture {
        color: var(--color-white) !important;
      }
    }

    &.action_key_remapped {
      background: var(--brand-color);
      box-shadow: var(--action-btn-primary-shadow);

      &:hover {
        background: var(--brand-color);
        box-shadow: var(--action-btn-primary-shadow);
      }
    }

    &.action_key_fn_remapped {
      background: #ff6b00;
      box-shadow: 0 4px 12px rgba(255, 107, 0, 0.4);

      &:hover {
        background: #ff6b00;
        box-shadow: 0 4px 12px rgba(255, 107, 0, 0.4);
      }
    }

    &.action_key_current {
      border-color: var(--color-brand-1);
      box-shadow: 0 0 2px var(--color-brand-1);
      background-color: var(--brand-color-soft-bg);
    }

    &.action_key_current_remapped {
      border-color: var(--color-white);
      box-shadow: 0 0 12px var(--color-brand-1);
      background: var(--gradient-trigger);

      &:hover {
        border-color: var(--color-white);
        box-shadow: 0 0 12px var(--color-brand-1);
        background: var(--gradient-trigger);
      }
    }
  }

  &.keyboard_view:has(.mgx_data) {
    .device_key {
      .texture {
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-top: 0;
        font-size: 10px;
        font-weight: 400;
        color: var(--color-text-hint);
      }
    }
  }
}

.HE2,
.HE3,
.HE3_SE,
.HE6_Ultra,
.Amber_35_8,
.Amber_35_8_Wired {
  height: 400px;
}

.HE2 {
  .right {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he2/operation/right_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/he2/operation/right_selected.png') no-repeat;
    }
  }

  .middle {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he2/operation/middle_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/he2/operation/middle_selected.png') no-repeat;
    }
  }

  .forward {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he2/operation/forward_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/he2/operation/forward_selected.png') no-repeat;
    }
  }

  .back {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he2/operation/back_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/he2/operation/back_selected.png') no-repeat;
    }
  }

  .dpi {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he2/operation/dpi_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/he2/operation/dpi_selected.png') no-repeat;
    }
  }
}

.HE3,
.HE3_SE {
  .right {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he3/operation/right_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/he3/operation/right_selected.png') no-repeat;
    }
  }

  .middle {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he3/operation/middle_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/he3/operation/middle_selected.png') no-repeat;
    }
  }

  .forward {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he3/operation/forward_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/he3/operation/forward_selected.png') no-repeat;
    }
  }

  .back {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he3/operation/back_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/he3/operation/back_selected.png') no-repeat;
    }
  }
}

.middle {
  z-index: 1;
}

.Amber_35_8 {
  .right {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/amber/operation/right_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/amber/operation/right_selected.png') no-repeat;
    }
  }

  .middle {
    cursor: pointer;
    z-index: 1;

    &:hover {
      background: url('@/assets/img/device/amber/operation/middle_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/amber/operation/middle_selected.png') no-repeat;
    }
  }

  .forward {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/amber/operation/forward_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/amber/operation/forward_selected.png') no-repeat;
    }
  }

  .back {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/amber/operation/back_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/amber/operation/back_selected.png') no-repeat;
    }
  }
}

.HE6_Ultra {
  .right {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he6_ultra/operation/right_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/he6_ultra/operation/right_selected.png') no-repeat;
    }
  }

  .middle {
    cursor: pointer;
    z-index: 1;

    &:hover {
      background: url('@/assets/img/device/he6_ultra/operation/middle_hover.png') no-repeat;
    }

    &:has(.active) {
      background: url('@/assets/img/device/he6_ultra/operation/middle_selected.png') no-repeat;
    }
  }

  .forward {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he6_ultra/operation/forward_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/he6_ultra/operation/forward_selected.png') no-repeat;
    }
  }

  .back {
    cursor: pointer;

    &:hover {
      background: url('@/assets/img/device/he6_ultra/operation/back_hover.png') no-repeat;
    }

    &:has(.active) {
      cursor: pointer;
      background: url('@/assets/img/device/he6_ultra/operation/back_selected.png') no-repeat;
    }
  }
}
</style>
